import { useState } from 'react';
import { useMaquinas, useCriarMaquina, useAtualizarMaquina, useDeletarMaquina } from '@/hooks/useMaquinas';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import {
  Plus,
  Trash2,
  Pencil
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import type { Maquina, StatusMaquina } from '@/types';
import { PrinterConnectionTest } from '@/components/admin/Maquinas/PrinterConnectionTest';

const statusConfig: Record<StatusMaquina, { label: string; variant: any }> = {
  DISPONIVEL: { label: 'Disponível', variant: 'success' },
  IMPRIMINDO: { label: 'Imprimindo', variant: 'info' },
  MANUTENCAO: { label: 'Manutenção', variant: 'warning' },
  INATIVA: { label: 'Inativa', variant: 'secondary' },
};

export function Maquinas() {
  const { data: maquinas, isLoading } = useMaquinas();
  const criarMaquina = useCriarMaquina();
  const atualizarMaquina = useAtualizarMaquina();
  const deletarMaquina = useDeletarMaquina();

  const [open, setOpen] = useState(false);
  const [selectedMaquina, setSelectedMaquina] = useState<Maquina | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    modelo: '',
    numeroSerie: '',
    valorAquisicao: '',
    depreciacaoMensal: '',
    custoHoraDepreciacao: '',
    ipAddress: '',
    apiKey: '',
    printerType: '',
  });

  const handleEdit = (maquina: Maquina) => {
    setSelectedMaquina(maquina);
    setFormData({
      nome: maquina.nome,
      modelo: maquina.modelo,
      numeroSerie: maquina.numeroSerie,
      valorAquisicao: String(maquina.valorAquisicao),
      depreciacaoMensal: String(maquina.depreciacaoMensal),
      custoHoraDepreciacao: String(maquina.custoHoraDepreciacao),
      ipAddress: maquina.ipAddress || '',
      apiKey: maquina.apiKey || '',
      printerType: maquina.printerType || '',
    });
    setOpen(true);
  };

  const cleanNumber = (val: string | number) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string' && val.trim() !== '') {
      // Se houver vírgula e ponto, assume formato BR (1.234,56)
      if (val.includes(',') && val.includes('.')) {
        return parseFloat(val.replace(/\./g, '').replace(',', '.')) || 0;
      }
      // Se houver apenas vírgula, assume decimal BR (1234,56)
      if (val.includes(',')) {
        return parseFloat(val.replace(',', '.')) || 0;
      }
      // Caso contrário, assume formato padrão (1234.56)
      return parseFloat(val) || 0;
    }
    return 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      valorAquisicao: cleanNumber(formData.valorAquisicao),
      depreciacaoMensal: cleanNumber(formData.depreciacaoMensal),
      custoHoraDepreciacao: cleanNumber(formData.custoHoraDepreciacao),
    };

    if (selectedMaquina) {
      await atualizarMaquina.mutateAsync({ id: selectedMaquina.id, data: payload });
    } else {
      await criarMaquina.mutateAsync(payload);
    }
    
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setSelectedMaquina(null);
    setFormData({
      nome: '',
      modelo: '',
      numeroSerie: '',
      valorAquisicao: '',
      depreciacaoMensal: '',
      custoHoraDepreciacao: '',
      ipAddress: '',
      apiKey: '',
      printerType: '',
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta máquina?')) {
      await deletarMaquina.mutateAsync(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Máquinas</h1>
          <p className="text-gray-500">Gerencie suas impressoras 3D</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button variant="nuvem" leftIcon={<Plus className="h-4 w-4" />}>
              Nova Máquina
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>{selectedMaquina ? 'Editar Máquina' : 'Cadastrar Máquina'}</DialogTitle>
              <DialogDescription>
                Configure as especificações e integrações da impressora 3D.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
                placeholder="Prusa MK4 #1"
              />
              <Input
                label="Modelo"
                value={formData.modelo}
                onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                required
                placeholder="Prusa MK4"
              />
              <Input
                label="Número de Série"
                value={formData.numeroSerie}
                onChange={(e) => setFormData({ ...formData, numeroSerie: e.target.value })}
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Valor de Aquisição (R$)"
                  type="number"
                  step="0.01"
                  value={formData.valorAquisicao}
                  onChange={(e) => setFormData({ ...formData, valorAquisicao: e.target.value })}
                  required
                />
                <Input
                  label="Depreciação Mensal (R$)"
                  type="number"
                  step="0.01"
                  value={formData.depreciacaoMensal}
                  onChange={(e) => setFormData({ ...formData, depreciacaoMensal: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Custo/Hora Depreciação (R$)"
                  type="number"
                  step="0.0001"
                  value={formData.custoHoraDepreciacao}
                  onChange={(e) => setFormData({ ...formData, custoHoraDepreciacao: e.target.value })}
                  placeholder="Calculado automaticamente"
                />
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">Explicação</label>
                  <p className="text-[10px] text-gray-500">Se deixar o custo hora zerado, o sistema calcula: (Depreciação Mensal / 200h úteis)</p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Integração Wi-Fi</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tipo</label>
                      <select 
                        className="w-full rounded-md border-gray-300 text-sm focus:border-orange-500 focus:ring-orange-500"
                        value={formData.printerType}
                        onChange={(e) => setFormData({ ...formData, printerType: e.target.value })}
                      >
                        <option value="">Nenhum</option>
                        <option value="OCTOPRINT">OctoPrint</option>
                        <option value="KLIPPER">Klipper</option>
                        <option value="FLASHFORGE">FlashForge</option>
                        <option value="BAMBU">Bambu Lab</option>
                        <option value="PRUSA">PrusaLink</option>
                        <option value="MARLIN">Marlin (Moonraker)</option>
                      </select>
                    </div>
                    <Input
                      label="IP da Máquina"
                      value={formData.ipAddress}
                      onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                      placeholder="192.168.1.100"
                    />
                  </div>
                  <Input
                    label="Chave API / Token"
                    type="password"
                    value={formData.apiKey}
                    onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                    placeholder="Cole aqui a chave da API"
                  />
                </div>
              </div>
              <Button type="submit" variant="nuvem" className="w-full" isLoading={criarMaquina.isPending || atualizarMaquina.isPending}>
                {selectedMaquina ? 'Atualizar Máquina' : 'Salvar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <div className="col-span-full text-center text-gray-500 py-8">Carregando...</div>
        ) : (
          maquinas?.map((maquina: Maquina) => (
            <Card key={maquina.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{maquina.nome}</CardTitle>
                    <p className="text-sm text-gray-500">{maquina.modelo}</p>
                  </div>
                  <Badge variant={statusConfig[maquina.status].variant}>
                    {statusConfig[maquina.status].label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Nº Série</span>
                  <span className="text-gray-900 font-mono">{maquina.numeroSerie}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Valor Aquisição</span>
                  <span className="text-gray-900">{formatCurrency(maquina.valorAquisicao)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Horas de Uso</span>
                  <span className="text-gray-900 font-mono">{maquina.horasUsoTotal}h</span>
                </div>
                <div className="flex justify-between text-sm pb-2 border-b border-gray-100">
                  <span className="text-gray-500">Taxa de Refugo</span>
                  <span className="text-gray-900">{maquina.taxaRefugoHistorica}%</span>
                </div>

                {maquina.ipAddress && (
                  <PrinterConnectionTest 
                    ip={maquina.ipAddress} 
                    tipo={maquina.printerType || 'DESCONHECIDO'} 
                  />
                )}

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full border-blue-200 text-blue-700 hover:bg-blue-50" 
                    leftIcon={<Pencil className="h-4 w-4" />}
                    onClick={() => handleEdit(maquina)}
                  >
                    Editar
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full border-red-200 text-red-700 hover:bg-red-50" 
                    leftIcon={<Trash2 className="h-4 w-4" />}
                    onClick={() => handleDelete(maquina.id)}
                  >
                    Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
